import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import styles from "./ElasticTabs.module.scss";
import { classNames } from "primereact/utils";

export interface IElasticTab {
  name: string;
  children: (shouldHideNavigation: boolean) => React.ReactNode;
  icon?: string;
  validator?: () => boolean;
  dataOnboarding?: string;
}

export interface IElasticTabsProps {
  tabs: IElasticTab[];
  defaultActiveIndex?: number;
  onTabChange?: (index: number) => void;
  className?: string;
  dataOnboarding?: string;
}

export const ElasticTabs: React.FC<IElasticTabsProps> = ({
  tabs,
  defaultActiveIndex,
  onTabChange,
  className,
  dataOnboarding,
}) => {
  // Filter tabs based on validators
  const availableTabs = useMemo(() => {
    return tabs.filter((tab) => {
      try {
        return tab.validator ? tab.validator() : true;
      } catch (error) {
        console.error("Validator error for tab:", tab.name, error);
        return false;
      }
    });
  }, [tabs]);

  // Determine initial active index from available tabs
  const getInitialActiveIndex = useCallback(() => {
    if (availableTabs.length === 0) return -1;

    // If defaultActiveIndex is provided and that tab is available, use it
    if (
      defaultActiveIndex !== undefined &&
      defaultActiveIndex < availableTabs.length
    ) {
      return defaultActiveIndex;
    }

    // Otherwise, use first available tab
    return 0;
  }, [availableTabs, defaultActiveIndex]);

  const [activeIndex, setActiveIndex] = useState(() => getInitialActiveIndex());
  const [userHasInteracted, setUserHasInteracted] = useState(false);
  const navigationRef = useRef<HTMLUListElement>(null);
  const selectorRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const shouldHideNavigation = availableTabs.length <= 1;

  const updateSelectorPosition = useCallback(
    (targetButton: HTMLButtonElement | null) => {
      if (!targetButton || !selectorRef.current || !navigationRef.current) {
        return;
      }

      // Calculate position relative to the navigation container
      let left = 0;
      let element: HTMLElement | null = targetButton;

      // Calculate the offset from the navigation container
      while (element && element !== navigationRef.current) {
        left += element.offsetLeft;
        element = element.offsetParent as HTMLElement;
      }

      const width = targetButton.offsetWidth;

      // Apply the position
      selectorRef.current.style.left = `${left}px`;
      selectorRef.current.style.width = `${width}px`;
    },
    []
  );

  // Handle tab click
  const handleTabClick = useCallback(
    (index: number) => {
      if (index === activeIndex) return;

      setActiveIndex(index);
      setUserHasInteracted(true);
      onTabChange?.(index);
    },
    [activeIndex, onTabChange, availableTabs]
  );

  useEffect(() => {
    const newActiveIndex = getInitialActiveIndex();
    if (
      newActiveIndex !== activeIndex &&
      newActiveIndex >= 0 &&
      !userHasInteracted
    ) {
      setActiveIndex(newActiveIndex);
      onTabChange?.(newActiveIndex);
    }
  }, [
    availableTabs,
    getInitialActiveIndex,
    activeIndex,
    userHasInteracted,
    onTabChange,
  ]);

  // Update selector position when active index changes
  useEffect(() => {
    if (!shouldHideNavigation && activeIndex >= 0) {
      const timer = setTimeout(() => {
        updateSelectorPosition(tabRefs.current[activeIndex]);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [activeIndex, shouldHideNavigation, updateSelectorPosition]);

  // Handle window resize
  useEffect(() => {
    if (!shouldHideNavigation) {
      const handleResize = () => {
        setTimeout(() => {
          if (activeIndex >= 0 && tabRefs.current[activeIndex]) {
            updateSelectorPosition(tabRefs.current[activeIndex]);
          }
        }, 100);
      };

      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }
  }, [shouldHideNavigation, activeIndex, updateSelectorPosition]);

  // Validate current active index is still valid after tab filtering
  useEffect(() => {
    if (activeIndex >= availableTabs.length && availableTabs.length > 0) {
      setActiveIndex(0);
    }
  }, [availableTabs.length, activeIndex]);

  if (availableTabs.length === 0) {
    return null;
  }

  return (
    <div className={classNames(styles.root, className)}>
      {!shouldHideNavigation && (
        <ul className={styles.root_navigation} ref={navigationRef} data-onboarding={dataOnboarding}>
          <div className={styles.root_selector} ref={selectorRef} />
          {availableTabs.map((tab, index) => (
            <li
              key={`tab-${tab.name}-${index}`}
              className={`${styles.root_tab} ${
                index === activeIndex ? styles.active : ""
              }`}
              data-onboarding={tab.dataOnboarding}
            >
              <button
                ref={(el) => (tabRefs.current[index] = el)}
                className={styles.root_button}
                onClick={() => handleTabClick(index)}
                type="button"
              >
                {tab.icon && (
                  <i className={`${tab.icon} ${styles.root_button_icon}`} />
                )}
                {tab.name}
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className={styles.root_children}>
        {availableTabs[activeIndex]?.children(shouldHideNavigation)}
      </div>
    </div>
  );
};
